import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';

/**
 * Generic time-series selector creator factory
 */
export function createTimeSeriesSelectors<TState extends { data: any; trackedItems: any[] }>(
  featureName: string,
  getRangeKey: (key: string) => string
) {
  const selectBaseState = createFeatureSelector<TState>(featureName);

  const selectGroupedData = createSelector(
    selectBaseState,
    (state) => state.data
  );

  const selectTrackedItems = createSelector(
    selectBaseState,
    (state) => state.trackedItems
  );

  /**
   * Generic parameterized selector for group data
   */
  const selectGroup = (
    groupBy: string,
    range: { key: string }
  ) => createSelector(
    selectGroupedData,
    (data) => {
      if (!data || !data[groupBy]) { return undefined; }
      return data[groupBy][getRangeKey(range.key)];
    }
  );

  /**
   * Specific group map selectors
   */
  const selectProjectGroupMap = createSelector(
    selectGroupedData,
    (data) => {
      if (!data || !data['projectId']) { return undefined; }
      return data['projectId'];
    }
  );

  const selectServiceGroupMap = createSelector(
    selectGroupedData,
    (data) => {
      // Handle both transaction-debit style ('Service') and direct ('stackId')
      const serviceKey = data?.['Service'] ? 'Service' : 'stackId';
      if (!data || !data[serviceKey]) { return undefined; }
      return data[serviceKey];
    }
  );

  const selectMetricGroupMap = createSelector(
    selectGroupedData,
    (data) => {
      // Handle both transaction-debit style ('Metric') and direct ('metricId')
      const metricKey = data?.['Metric'] ? 'Metric' : 'metricId';
      if (!data || !data[metricKey]) { return undefined; }
      return data[metricKey];
    }
  );

  return {
    selectBaseState,
    selectGroupedData,
    selectTrackedItems,
    selectGroup,
    selectProjectGroupMap,
    selectServiceGroupMap,
    selectMetricGroupMap
  };
}

/**
 * Create additional specialized selectors for specific use cases
 */
export function createTimeSeriesSpecializedSelectors<
  TState extends { data: any; trackedItems: any[] },
  TPeriodCosts = any
>(
  baseSelectors: ReturnType<typeof createTimeSeriesSelectors>,
  config?: {
    periodCostsSelector?: (state: TState) => TPeriodCosts;
    customSelectors?: { [key: string]: MemoizedSelector<any, any> };
  }
) {
  const specializedSelectors: any = {};

  // Add period costs selectors if provided
  if (config?.periodCostsSelector) {
    specializedSelectors.selectPeriodCosts = createSelector(
      baseSelectors.selectBaseState,
      (state: TState) => config.periodCostsSelector?.(state)
    );

    specializedSelectors.selectClientPeriodCosts = createSelector(
      specializedSelectors.selectPeriodCosts,
      (periodCosts: TPeriodCosts) => (periodCosts as any)?.client
    );

    specializedSelectors.selectProjectPeriodCostsMap = createSelector(
      specializedSelectors.selectPeriodCosts,
      (periodCosts: TPeriodCosts) => (periodCosts as any)?.projects
    );

    specializedSelectors.selectServicePeriodCostsMap = createSelector(
      specializedSelectors.selectPeriodCosts,
      (periodCosts: TPeriodCosts) => (periodCosts as any)?.services
    );
  }

  // Add custom selectors if provided
  if (config?.customSelectors) {
    Object.assign(specializedSelectors, config.customSelectors);
  }

  return {
    ...baseSelectors,
    ...specializedSelectors
  };
}